"use client"

import { useEffect, useState } from "react"
import { useAuthStore } from "@/lib/auth-store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Folder, Plus, Loader2, CheckCircle2, Users, Search } from "@/lib/icons"
import type { CollectionListItem } from "./types"
import { useTranslations } from "next-intl"
import { cn } from "@/lib/utils"

interface SaveToGoonfolioDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Payload for POST /api/collections/[id]/items — either a script pointer
   * or a denormalized external-content snapshot (e.g. a BooruPost). */
  item:
    | { itemType: "script"; scriptId: string }
    | { itemType: "external_content"; externalContent: Record<string, any> }
}

// "Save this into a folio": one of your own folios (or a new one made inline), or a community folio other people post into. A post
// added to a community folio is anonymous unless the person ticks "show my name" (off every time the dialog opens).
export function SaveToGoonfolioDialog({ open, onOpenChange, item }: SaveToGoonfolioDialogProps) {
  const t = useTranslations("saveToGoonfolioDialog")
  const tc = useTranslations("communityFolios")
  const { user } = useAuthStore()
  const [tab, setTab] = useState<"mine" | "community">("mine")
  const [collections, setCollections] = useState<CollectionListItem[] | null>(null)
  const [community, setCommunity] = useState<CollectionListItem[] | null>(null)
  const [communityQuery, setCommunityQuery] = useState("")
  const [showName, setShowName] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set())
  const [creatingName, setCreatingName] = useState("")
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !user) return
    setCollections(null)
    setSavedIds(new Set())
    setError(null)
    setShowName(false)
    fetch(`/api/collections?userId=${user.id}`)
      .then((r) => (r.ok ? r.json() : null))
      // Your own community folios are listed under Community, with everyone else's.
      .then((data) => setCollections(((data?.collections ?? []) as CollectionListItem[]).filter((c) => c.kind !== "community")))
      .catch(() => setCollections([]))
  }, [open, user])

  useEffect(() => {
    if (!open || !user || tab !== "community") return
    setCommunity(null)
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ scope: "explore", kind: "community", sort: "top" })
      if (communityQuery.trim()) params.set("q", communityQuery.trim())
      fetch(`/api/collections?${params.toString()}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => setCommunity(data?.collections ?? []))
        .catch(() => setCommunity([]))
    }, 250)
    return () => clearTimeout(timer)
  }, [open, user, tab, communityQuery])

  async function saveInto(collectionId: string, isCommunity = false) {
    setSavingId(collectionId)
    setError(null)
    try {
      const res = await fetch(`/api/collections/${collectionId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isCommunity ? { ...item, showName } : item),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || "Failed to save")
      }
      setSavedIds((prev) => new Set(prev).add(collectionId))
    } catch (err: any) {
      setError(err?.message ?? "Failed to save")
    } finally {
      setSavingId(null)
    }
  }

  async function createAndSave() {
    if (!creatingName.trim()) return
    setCreating(true)
    setError(null)
    try {
      const res = await fetch("/api/collections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: creatingName.trim(), isPublic: true }),
      })
      if (!res.ok) throw new Error(t("failedToCreateFolio"))
      const { collection } = await res.json()
      setCreatingName("")
      await saveInto(collection.id)
      setCollections((prev) => [{ ...collection, itemCount: 0, previewThumbnails: [], coverImageUrl: null }, ...(prev || [])])
    } catch (err: any) {
      setError(err?.message ?? t("failedToCreateFolio"))
    } finally {
      setCreating(false)
    }
  }

  if (!user) return null

  const folioButton = (c: CollectionListItem, isCommunity: boolean) => (
    <Button
      key={c.id}
      variant="outline"
      className="w-full justify-between"
      disabled={savingId === c.id || savedIds.has(c.id)}
      onClick={() => saveInto(c.id, isCommunity)}
    >
      <span className="flex items-center gap-2 truncate">
        {isCommunity ? <Users className="h-4 w-4 shrink-0" /> : <Folder className="h-4 w-4 shrink-0" />}
        <span className="truncate">{c.name}</span>
        {isCommunity && <span className="text-xs text-muted-foreground shrink-0">{c.itemCount}</span>}
      </span>
      {savingId === c.id ? (
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
      ) : savedIds.has(c.id) ? (
        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
      ) : null}
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("saveToGoonfolio")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="inline-flex w-full rounded-lg border p-1 gap-1" role="tablist">
            {(["mine", "community"] as const).map((value) => (
              <Button
                key={value}
                role="tab"
                aria-selected={tab === value}
                size="sm"
                variant={tab === value ? "default" : "ghost"}
                onClick={() => { setTab(value); setError(null) }}
                className="flex-1 h-7 text-xs gap-1.5"
              >
                {value === "mine" ? <Folder className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
                {value === "mine" ? tc("yourFolios") : tc("communityFolios")}
              </Button>
            ))}
          </div>

          {tab === "mine" ? (
            <>
              {!collections ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1.5">
                  {collections.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">{t("noFoliosYetCreateOneBelow")}</p>
                  )}
                  {collections.map((c) => folioButton(c, false))}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t">
                <Input
                  placeholder={t("newFolioName")}
                  value={creatingName}
                  onChange={(e) => setCreatingName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createAndSave()}
                />
                <Button onClick={createAndSave} disabled={!creatingName.trim() || creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={communityQuery}
                  onChange={(e) => setCommunityQuery(e.target.value)}
                  placeholder={tc("searchCommunity")}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {!community ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="community-folio-list">
                  {community.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{tc("noCommunityFolios")}</p>}
                  {community.map((c) => folioButton(c, true))}
                </div>
              )}
              <div className={cn("flex items-start gap-2 pt-2 border-t")}>
                <Checkbox id="community-show-name" checked={showName} onCheckedChange={(v) => setShowName(v === true)} className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="community-show-name" className="text-sm font-normal">{tc("showMyName")}</Label>
                  <p className="text-xs text-muted-foreground">{tc("showMyNameHint")} {tc("rewardNote")}</p>
                </div>
              </div>
            </>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  )
}
